import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";

export default function Pagination({ page, setPage, totalPages }) {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      let startPage = Math.max(2, page - 1);
      let endPage = Math.min(totalPages - 1, page + 1);

      // Adjust if we're near the start
      if (page <= 3) {
        endPage = 4;
      }

      // Adjust if we're near the end
      if (page >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center justify-center gap-1 py-4 px-2">
      {/* First page button */}
      <button
        onClick={() => setPage(1)}
        disabled={page === 1}
        className={`p-2 rounded-lg transition-colors ${
          page === 1
            ? "text-gray-400 cursor-not-allowed"
            : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
        }`}
        title="First page"
      >
        <MdKeyboardDoubleArrowLeft size={20} />
      </button>

      {/* Previous page button */}
      <button
        onClick={() => setPage(page - 1)}
        disabled={page === 1}
        className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-1 ${
          page === 1
            ? "text-gray-400 cursor-not-allowed"
            : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
        }`}
      >
        <MdKeyboardArrowLeft size={20} />
        <span className="hidden sm:inline">Previous</span>
      </button>

      {/* Page numbers */}
      <div className="flex items-center gap-1">
        {pageNumbers.map((pageNum, index) => {
          if (pageNum === "...") {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-3 py-2 text-gray-500"
              >
                ...
              </span>
            );
          }

          return (
            <button
              key={pageNum}
              onClick={() => setPage(pageNum)}
              className={`min-w-[40px] px-3 py-2 rounded-lg transition-colors font-medium ${
                page === pageNum
                  ? "bg-blue-500 text-white shadow-md"
                  : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              }`}
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      {/* Next page button */}
      <button
        onClick={() => setPage(page + 1)}
        disabled={page === totalPages}
        className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-1 ${
          page === totalPages
            ? "text-gray-400 cursor-not-allowed"
            : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
        }`}
      >
        <span className="hidden sm:inline">Next</span>
        <MdKeyboardArrowRight size={20} />
      </button>

      {/* Last page button */}
      <button
        onClick={() => setPage(totalPages)}
        disabled={page === totalPages}
        className={`p-2 rounded-lg transition-colors ${
          page === totalPages
            ? "text-gray-400 cursor-not-allowed"
            : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
        }`}
        title="Last page"
      >
        <MdKeyboardDoubleArrowRight size={20} />
      </button>
    </div>
  );
}
